import { useMemo } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { queryFnFetchErrorWrapper, throwFetchError } from 'src/api/utils/errorHandlers';
import { mergeQueryOptions, retryOnTransient } from 'src/api/utils/helpers';
import { queryNames } from 'src/api/utils/queryNames';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { CustomQueryOptions, FetchError } from 'src/types/TApiQueries';
import {
  SharesListResponse,
  ShareResponse,
  SharesListParams,
  ShareCommentsResponse,
  ShareApprovalsResponse,
  ShareAnalyticsResponse,
} from 'src/types/TShare';
import {
  listShares,
  getShareAuthenticated,
  getShareComments,
  getShareApprovals,
  getShareAnalytics,
  getShareCount,
  ShareCountResponse,
  AnalyticsParams,
} from '../functions/shareApi';

/**
 * Count-only counterpart of `useSharesListQuery`. Hits the same
 * endpoint but with `count_only=y` — the backend skips loading
 * share rows and returns just `shares_found`. Cached under a
 * dedicated query key so the regular shares list (which needs full
 * rows) is not invalidated.
 */
export function useShareCountQuery(
  queryOptions?: UseQueryOptions<ShareCountResponse, FetchError>,
) {
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getShareCount(accessToken)),
    [accessToken],
  );

  return useQuery<ShareCountResponse, FetchError>(
    [queryNames.share, 'count'],
    queryFn,
    {
      staleTime: 60_000,
      retry: retryOnTransient,
      ...queryOptions,
    },
  );
}

/**
 * Hook to fetch the list of shares for the authenticated user
 */
export function useSharesListQuery(
  params?: SharesListParams,
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<SharesListResponse, FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions(options, queryOptions);
  const accessToken = useAccessToken();

  // Create a stable serialized params string for query key
  const paramsKey = useMemo(() => JSON.stringify(params || {}), [params]);

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => listShares(accessToken, params)),
    [accessToken, paramsKey],
  );

  const query = useQuery<SharesListResponse, FetchError>(
    [queryNames.share, 'list', paramsKey],
    queryFn,
    {
      ...actualQueryOptions,
      staleTime: 30000, // 30 seconds
      keepPreviousData: true,
    },
  );

  if (options?.handleErrors && query.status === 'error') {
    if (!query.isRefetchError) {
      throwFetchError(query.error);
    }
  }

  return query;
}

/**
 * Hook to fetch a single share by ID (authenticated - for owner)
 */
export function useShareQuery(
  shareId?: string,
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<ShareResponse, FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions(options, queryOptions);
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getShareAuthenticated(accessToken, shareId!)),
    [accessToken, shareId],
  );

  const query = useQuery<ShareResponse, FetchError>(
    [queryNames.share, shareId],
    queryFn,
    {
      ...actualQueryOptions,
      enabled: !!shareId,
    },
  );

  if (options?.handleErrors && query.status === 'error') {
    if (!query.isRefetchError) {
      throwFetchError(query.error);
    }
  }

  return query;
}

/**
 * Hook to fetch comments for a share (authenticated - owner only)
 */
export function useShareCommentsQuery(
  shareId?: string,
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<ShareCommentsResponse, FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions(options, queryOptions);
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getShareComments(accessToken, shareId!)),
    [accessToken, shareId],
  );

  const query = useQuery<ShareCommentsResponse, FetchError>(
    [queryNames.share, shareId, 'comments'],
    queryFn,
    {
      ...actualQueryOptions,
      enabled: !!shareId,
      staleTime: 30000, // 30 seconds
    },
  );

  if (options?.handleErrors && query.status === 'error') {
    if (!query.isRefetchError) {
      throwFetchError(query.error);
    }
  }

  return query;
}

/**
 * Hook to fetch approvals for a share (authenticated - owner only)
 */
export function useShareApprovalsQuery(
  shareId?: string,
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<ShareApprovalsResponse, FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions(options, queryOptions);
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getShareApprovals(accessToken, shareId!)),
    [accessToken, shareId],
  );

  const query = useQuery<ShareApprovalsResponse, FetchError>(
    [queryNames.share, shareId, 'approvals'],
    queryFn,
    {
      ...actualQueryOptions,
      enabled: !!shareId,
      staleTime: 30000, // 30 seconds
    },
  );

  if (options?.handleErrors && query.status === 'error') {
    if (!query.isRefetchError) {
      throwFetchError(query.error);
    }
  }

  return query;
}

/**
 * Hook to fetch analytics for a share (authenticated - owner only)
 */
export function useShareAnalyticsQuery(
  shareId?: string,
  params?: AnalyticsParams,
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<ShareAnalyticsResponse, FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions(options, queryOptions);
  const accessToken = useAccessToken();

  // Create a stable serialized params string for query key
  const paramsKey = useMemo(() => JSON.stringify(params || {}), [params]);

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getShareAnalytics(accessToken, shareId!, params)),
    [accessToken, shareId, paramsKey],
  );

  const query = useQuery<ShareAnalyticsResponse, FetchError>(
    [queryNames.share, shareId, 'analytics', paramsKey],
    queryFn,
    {
      ...actualQueryOptions,
      enabled: !!shareId,
      staleTime: 60000, // 1 minute
    },
  );

  if (options?.handleErrors && query.status === 'error') {
    if (!query.isRefetchError) {
      throwFetchError(query.error);
    }
  }

  return query;
}

